import React from 'react';

import { useQuery } from '@tanstack/react-query';
import { mountRootParcel } from 'single-spa';
import Parcel from 'single-spa-react/parcel';

import { createReactRenderer } from 'helpers/react-renderer';
import { InvoicingConfig } from 'root/apps/QuoteMachineIntegration/common';
import { useBalanceDriftDetection } from 'root/apps/QuoteMachineIntegration/hooks/useBalanceDriftDetection';
import CustomerAccountService from 'root/apps/QuoteMachineIntegration/services/CustomerAccountService';
import { CustomerCreditAccountDetailsProps } from 'root/apps/QuoteMachineIntegration/state/CustomerCreditAccountDetailsProps';
import { SystemJS } from 'root/apps/QuoteMachineIntegration/SystemJS';
import { getSessionData } from 'root/apps/QuoteMachineIntegration/utils/SessionData';
import Modules from 'root/apps/shared_modules/Modules';
import { datadogRum } from 'root/datadog/datadogRum';

import AutomatedAccountReceivablesService from '../../services/AutomatedAccountReceivablesService';
import { AutomatedAccountReceivablesManagementType } from '../../state/AutomatedAccountReceivablesManagementType';
import { AutomatedARManagementStatus } from '../../state/AutomatedARManagementStatus';
import { handleClickOnAutomaticARRecipient } from '../../utils/AutomaticARRecipientRedirect';

const CustomerCreditAccountDetailsParcel = ({
  customerId,
  customerFullName,
  customerEmail,
  creditAccountId,
  currentCreditLimit,
  lsrBalanceOwed,
  lsrDepositBalance,
}: CustomerCreditAccountDetailsProps) => {
  const [automationStatus, setAutomationStatus] = React.useState(AutomatedARManagementStatus.OFF);

  const { data: invoicingData } = useQuery({
    queryKey: ['creditAccountBalance', creditAccountId],
    queryFn: () =>
      CustomerAccountService.getCustomerAccount(creditAccountId, String(currentCreditLimit ?? '')),
    retry: false,
    cacheTime: 0,
    refetchOnMount: true,
  });

  useBalanceDriftDetection({
    creditAccountID: creditAccountId,
    invoicingData,
    lsrBalanceOwed,
    lsrDepositBalance,
  });

  const customerHasEmail = !!customerEmail;

  React.useEffect(() => {
    const fetchAutoARStatus = async () => {
      try {
        const res = await AutomatedAccountReceivablesService.getAutomatedAccountReceivablesStatus(
          AutomatedAccountReceivablesManagementType.CUSTOMER,
          creditAccountId
        );
        setAutomationStatus(res);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn(error);
        setAutomationStatus(AutomatedARManagementStatus.ERROR);
      }
    };
    fetchAutoARStatus();
  }, [creditAccountId, customerId]);

  return (
    <Parcel
      mountParcel={mountRootParcel}
      skipCSSUnmount={true}
      sessionData={getSessionData()}
      handleError={(err: Error) => datadogRum.addError(err)}
      customerId={customerId}
      customerFullName={customerFullName}
      creditAccountId={creditAccountId}
      currentCreditLimit={currentCreditLimit}
      getCreditAccountDetails={() =>
        CustomerAccountService.getCustomerAccount(creditAccountId, String(currentCreditLimit))
      }
      automatedAccountReceivablesStatus={automationStatus}
      setAutomationStatus={setAutomationStatus}
      updateAutomatedAccountReceivablesStatus={(
        context: AutomatedAccountReceivablesManagementType,
        automationEnabled: boolean,
        creditAccountId: string
      ) =>
        AutomatedAccountReceivablesService.updateAutomatedAccountReceivablesStatus(
          context,
          automationEnabled,
          creditAccountId
        )
      }
      fetchActions={() => AutomatedAccountReceivablesService.fetchActions(creditAccountId)}
      onRecipientClick={(recipientId: string) =>
        handleClickOnAutomaticARRecipient(recipientId, 'details', false)
      }
      showCustomerProfileMissingEmailBanner={!customerHasEmail}
      config={async () =>
        (await SystemJS.import<InvoicingConfig>('@lightspeed/invoicing')).default
          .CustomerCreditAccountDetailsConfig
      }
    />
  );
};

const CustomerCreditAccountDetails = (props: CustomerCreditAccountDetailsProps) => {
  if (Modules.hasModule('customers.invoicing_integration.customer_profile_web_components')) {
    return null;
  }

  return <CustomerCreditAccountDetailsParcel {...props} />;
};

const RenderedComponent = createReactRenderer({
  component: CustomerCreditAccountDetails,
  selector: '[data-react-root="CustomerCreditAccountDetails"]',
});

export default CustomerCreditAccountDetails;

history.onChangeState(
  [
    {
      name: ['customer.views.customer'],
      form_name: 'view',
      tab: 'account',
      id: undefined,
    },
  ],
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);
